import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, ArrowUpRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiService from '../api/aiService';

const ChatAssistant = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([
        { id: 1, text: "Hello! I'm your Xpanse AI Assistant. How can I help you today?", sender: 'bot' }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const hasDragged = useRef(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        if (showTooltip) {
            const timer = setTimeout(() => setShowTooltip(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [showTooltip]);

    const handleSend = async (e) => {
        e.preventDefault();
        const currentMsg = message.trim();
        if (!currentMsg) return;

        const newUserMessage = { id: Date.now(), text: currentMsg, sender: 'user' };
        setMessages(prev => [...prev, newUserMessage]);
        setMessage('');
        setIsTyping(true);

        try {
            const responseText = await aiService.sendMessage(currentMsg);
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                text: responseText,
                sender: 'bot'
            }]);
        } catch (error) {
            console.error('Error in Chat Assistant:', error);
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                text: "Sorry, I'm having trouble connecting. Please try again later.",
                sender: 'bot'
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleBubbleClick = () => {
        if (!hasDragged.current) {
            setIsOpen(prev => !prev);
            setShowTooltip(false);
        }
        hasDragged.current = false; // Reset after handling click
    };

    // Shared radiation ring base style
    const radiationBase = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '50%',
        pointerEvents: 'none',
    };

    return (
        <>
            {/* Global keyframes injected once */}
            <style>{`
                @keyframes radiation-pulse {
                    0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; border-color: rgba(59,130,246,0.6); }
                    50%  { opacity: 0.4; border-color: rgba(59,130,246,0.4); }
                    100% { transform: translate(-50%, -50%) scale(2);   opacity: 0;   border-color: rgba(59,130,246,0); }
                }
                @keyframes glow-pulse {
                    0%, 100% { box-shadow: 0 0 10px rgba(59,130,246,0.5), 0 0 20px rgba(59,130,246,0.3), 0 0 30px rgba(59,130,246,0.1); }
                    50%      { box-shadow: 0 0 20px rgba(59,130,246,0.7), 0 0 40px rgba(59,130,246,0.4), 0 0 60px rgba(59,130,246,0.2); }
                }
                @keyframes blink {
                    0%, 80%, 100% { opacity: 0; transform: scale(0.8); }
                    40%           { opacity: 1; transform: scale(1); }
                }
                .typing-dot {
                    width: 8px; height: 8px; border-radius: 50%;
                    background: #94a3b8; display: inline-block; margin: 0 2px;
                    animation: blink 1.4s infinite ease-in-out;
                }
                .typing-dot:nth-child(2) { animation-delay: 0.2s; }
                .typing-dot:nth-child(3) { animation-delay: 0.4s; }
                .chat-messages::-webkit-scrollbar { width: 4px; }
                .chat-messages::-webkit-scrollbar-track { background: transparent; }
                .chat-messages::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                .chat-nav-link {
                    color: #3b82f6; text-decoration: underline;
                    display: inline-flex; align-items: center;
                }
                .chat-nav-link:hover { color: #1d4ed8; }
                .markdown-content p  { margin: 0 0 6px; line-height: 1.5; }
                .markdown-content ul { padding-left: 18px; margin: 4px 0; }
                .markdown-content li { margin-bottom: 2px; }
                .markdown-content code {
                    background: rgba(0,0,0,0.08); border-radius: 4px;
                    padding: 1px 5px; font-size: 12px;
                }
                .bubble-send-btn:hover  { background: #2563eb !important; }
                .bubble-send-btn:disabled { opacity: 0.4; cursor: not-allowed !important; }
            `}</style>

            {/* ── Chat Window ── */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        style={{
                            position: 'fixed',
                            bottom: '100px',
                            right: '16px',
                            width: '360px',
                            height: '520px',
                            borderRadius: '20px',
                            background: '#ffffff',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(59,130,246,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            zIndex: 9998,
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            flexShrink: 0,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff',
                                }}>
                                    <Bot size={20} />
                                </div>
                                <div>
                                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '15px', lineHeight: 1.2 }}>
                                        Xpanse AI
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span style={{
                                            width: '7px', height: '7px', borderRadius: '50%',
                                            background: '#4ade80', display: 'inline-block',
                                        }} />
                                        Online & Ready to help
                                    </div>
                                </div>
                            </div>
                            <div
                                onClick={() => setIsOpen(false)}
                                style={{
                                    width: '30px', height: '30px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', color: '#fff',
                                    transition: 'background 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                            >
                                <X size={16} />
                            </div>
                        </div>

                        {/* Messages */}
                        <div
                            className="chat-messages"
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '16px 14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                background: '#f8fafc',
                            }}
                        >
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                    }}
                                >
                                    <div style={{
                                        maxWidth: '80%',
                                        padding: '10px 14px',
                                        borderRadius: msg.sender === 'user'
                                            ? '18px 18px 4px 18px'
                                            : '18px 18px 18px 4px',
                                        background: msg.sender === 'user'
                                            ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                                            : '#ffffff',
                                        color: msg.sender === 'user' ? '#fff' : '#1e293b',
                                        fontSize: '13.5px',
                                        lineHeight: '1.5',
                                        boxShadow: msg.sender === 'user'
                                            ? '0 2px 8px rgba(59,130,246,0.3)'
                                            : '0 2px 8px rgba(0,0,0,0.06)',
                                        border: msg.sender === 'bot' ? '1px solid rgba(0,0,0,0.06)' : 'none',
                                        wordBreak: 'break-word',
                                    }}>
                                        {msg.sender === 'bot' ? (
                                            <div className="markdown-content">
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                        a: ({ href, children, ...props }) => {
                                                            const isInternal = href && href.startsWith('/');
                                                            if (isInternal) {
                                                                return (
                                                                    <a
                                                                        href={href}
                                                                        className="chat-nav-link"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            navigate(href);
                                                                            setIsOpen(false);
                                                                        }}
                                                                        {...props}
                                                                    >
                                                                        {children}
                                                                        <ArrowUpRight size={14} style={{ marginLeft: '4px', flexShrink: 0 }} />
                                                                    </a>
                                                                );
                                                            }
                                                            return (
                                                                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                                                    {children}
                                                                </a>
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {msg.text}
                                                </ReactMarkdown>
                                            </div>
                                        ) : (
                                            msg.text
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Typing indicator */}
                            {isTyping && (
                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <div style={{
                                        padding: '12px 16px',
                                        borderRadius: '18px 18px 18px 4px',
                                        background: '#ffffff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                    }}>
                                        <span className="typing-dot" />
                                        <span className="typing-dot" />
                                        <span className="typing-dot" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <form
                            onSubmit={handleSend}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 14px',
                                borderTop: '1px solid #e2e8f0',
                                background: '#ffffff',
                                flexShrink: 0,
                            }}
                        >
                            <input
                                type="text"
                                placeholder="Type your message..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                style={{
                                    flex: 1,
                                    padding: '10px 14px',
                                    borderRadius: '24px',
                                    border: '1.5px solid #e2e8f0',
                                    outline: 'none',
                                    fontSize: '13.5px',
                                    color: '#1e293b',
                                    background: '#f8fafc',
                                    transition: 'border-color 0.2s',
                                }}
                                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                            />
                            <button
                                type="submit"
                                className="bubble-send-btn"
                                disabled={!message.trim() || isTyping}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    transition: 'background 0.2s',
                                }}
                            >
                                <Send size={16} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Tooltip ── */}
            <AnimatePresence>
                {showTooltip && !isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            position: 'fixed',
                            bottom: '105px',
                            right: '40px',
                            background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                            color: '#fff',
                            padding: '10px 16px',
                            borderRadius: '12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 20px rgba(59,130,246,0.3), 0 0 0 1px rgba(59,130,246,0.2)',
                            zIndex: 9997,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            pointerEvents: 'none',
                        }}
                    >
                        <Bot size={15} style={{ color: '#3b82f6', flexShrink: 0 }} />
                        <span>Hi! I'm Xpanse AI - Ask me anything!</span>
                        {/* Arrow */}
                        <div style={{
                            position: 'absolute',
                            bottom: '-6px',
                            right: '28px',
                            width: '12px',
                            height: '12px',
                            background: '#0f172a',
                            transform: 'rotate(45deg)',
                            borderRight: '1px solid rgba(59,130,246,0.2)',
                            borderBottom: '1px solid rgba(59,130,246,0.2)',
                        }} />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Draggable Chat Bubble ── */}
            <motion.div
                drag
                dragMomentum={false}
                dragElastic={0}
                onDragStart={() => {
                    hasDragged.current = false;
                }}
                onDrag={(event, info) => {
                    // Only mark as dragged if there's significant movement
                    if (Math.abs(info.offset.x) > 3 || Math.abs(info.offset.y) > 3) {
                        hasDragged.current = true;
                    }
                    setIsDragging(true);
                }}
                onDragEnd={() => {
                    setIsDragging(false);
                }}
                onClick={handleBubbleClick}
                whileHover={{ scale: isDragging ? 1 : 1.08 }}
                whileTap={{ scale: 0.92 }}
                style={{
                    position: 'fixed',
                    bottom: '32px',
                    right: '40px',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    color: '#fff',
                    zIndex: 9999,
                    touchAction: 'none',
                    animation: 'glow-pulse 2s ease-in-out infinite',
                    boxShadow: '0 0 20px rgba(59,130,246,0.5), 0 0 40px rgba(59,130,246,0.3)',
                    userSelect: 'none',
                }}
            >
                {/* Radiation rings */}
                {[
                    { size: '130%', delay: '0s',    opacity: 0.5 },
                    { size: '160%', delay: '0.6s',  opacity: 0.35 },
                    { size: '190%', delay: '1.2s',  opacity: 0.2 },
                ].map((ring, i) => (
                    <div
                        key={i}
                        style={{
                            ...radiationBase,
                            width: ring.size,
                            height: ring.size,
                            border: `2px solid rgba(59,130,246,${ring.opacity})`,
                            animation: `radiation-pulse 2s ease-out ${ring.delay} infinite`,
                        }}
                    />
                ))}

                {/* Icon */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                    {isOpen ? <X size={26} /> : <MessageSquare size={26} />}
                </div>
            </motion.div>
        </>
    );
};

export default ChatAssistant;